import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { LimitedVirtualizedList } from '@eo/flatlist'
import { trpc } from '@/lib/trpc'
import { MediaCard, type BoxCounts } from '@/components/MediaCard'
import type { Media } from '@/types/api'

// Counts ride ON the list items (not in the render closure): virtualized rows
// re-render on data changes, so merging counts into the array is what makes
// the chips appear when the aggregate query resolves.
type MediaWithCounts = Media & { _counts?: BoxCounts }

function MediaGrid({
  scopeId,
  skillId,
  organizationId,
}: {
  scopeId: string
  skillId: string
  organizationId: string
}) {
  const navigate = useNavigate()
  const { data: medias = [], isLoading, error } = trpc.media.getByScopeId.useQuery(
    { scopeId },
    { staleTime: 5 * 60 * 1000 }
  )

  // Per-media category counts for the card chips (one aggregate query).
  const { data: boxCounts = {} } = trpc.annotations.getBoxCounts.useQuery(
    { mediaIds: medias.map((m) => m.id) },
    { enabled: medias.length > 0, staleTime: 60 * 1000 }
  )
  const items = useMemo<MediaWithCounts[]>(
    () => medias.map((m) => ({ ...m, _counts: boxCounts[m.id] })),
    [medias, boxCounts]
  )

  if (isLoading) {
    return <MediaGridSkeleton />
  }

  if (error) {
    return <p className="text-body-default text-fill-destructive">Failed to load media. Please try again.</p>
  }

  if (medias.length === 0) {
    return <p className="text-body-default text-text-secondary">No media found for this skill.</p>
  }

  return (
    <div className="skill-media-grid-container w-full overflow-hidden">
      <LimitedVirtualizedList<MediaWithCounts>
        data={items}
        fixedItemWidth={156}
        rowHeight={252}
        columnGap={20}
        rowGap={20}
        rowsToShow={2}
        controlsAlignment="center"
        showMoreText="Show More"
        showLessText="Show Less"
        showCount={true}
        buttonClassName="!bg-text-primary !text-text-inverse text-label-default px-6 py-2 rounded-md transition-opacity hover:!opacity-90"
        renderItem={({ item: media }) => (
          <MediaCard
            media={media}
            organizationId={organizationId}
            isSelected={false}
            counts={media._counts}
            onChipClick={(kind) => {
              navigate(`/skills/${skillId}/${media.id}?filters=${kind}`)
            }}
            onClick={() => {
              navigate(`/skills/${skillId}/${media.id}`)
            }}
          />
        )}
        keyExtractor={(item) => item.id}
      />
    </div>
  )
}

function MediaGridSkeleton() {
  return (
    <div className="flex flex-wrap gap-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="h-[156px] w-[156px] animate-pulse rounded-sm bg-surface-tertiary" />
      ))}
    </div>
  )
}

export function SkillDetailPage() {
  const { id } = useParams<{ id: string }>()

  const { data: skill, isLoading, error } = trpc.skills.getById.useQuery(
    { id: id! },
    { enabled: !!id, staleTime: 5 * 60 * 1000 }
  )

  if (isLoading) {
    return (
      <main className="min-h-screen bg-surface-primary p-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 h-[30px] w-64 animate-pulse rounded bg-surface-tertiary" />
          <MediaGridSkeleton />
        </div>
      </main>
    )
  }

  if (error || !skill) {
    return (
      <main className="min-h-screen bg-surface-primary p-8">
        <div className="mx-auto max-w-7xl">
          <h1 className="text-h1 mb-6 text-fill-destructive">Skill not found</h1>
          <p className="text-body-default text-text-secondary">The requested skill could not be loaded.</p>
        </div>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-surface-primary p-8">
      <div className="mx-auto max-w-7xl">
        <h1 className="text-h1 mb-6 text-text-primary">{skill.scope_name}</h1>

        <MediaGrid
          scopeId={skill.scope_id}
          skillId={id!}
          organizationId={skill.organization_id}
        />
      </div>
    </main>
  )
}
